
import { Component, OnInit, Inject, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminService, Employee, Customer, Project, Activity, Team, Unit, Costcenter } from '../../core/services/admin.service';
import { ViewStateService } from '../../core/services/view-state.service';

@Component({
  selector: 'app-ng-admin',
  template: require('./ng-admin.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./ng-admin.component.scss').default],
  standalone: false,
})
export class NgAdminComponent implements OnInit {
  // Data
  employees: Employee[] = [];
  customers: Customer[] = [];
  projects: Project[] = [];
  activities: Activity[] = [];
  teams: Team[] = [];

  // Units are reference data from DB.
  units: Unit[] = [];
  // Costcenters are reference data from DB. The current database model still stores
  // external_id in employee/activity costcenter columns, so admin writes external_id values.
  costcenters: Costcenter[] = [];

  // Active tab
  activeTab: 'employee' | 'customer' | 'project' | 'activity' | 'activityPrice' | 'team' | 'salary' | 'bulkCreate' | 'audit' = 'employee';

  // Loading state
  loading = false;

  private destroyRef = inject(DestroyRef);
  private readonly viewKey = '/admin';

  constructor(
    @Inject(AdminService) private adminService: AdminService,
    @Inject(ViewStateService) private viewState: ViewStateService
  ) {}

  ngOnInit(): void {
    const savedTab = this.viewState.getViewTab(this.viewKey);
    if (this.isValidTab(savedTab)) {
      this.activeTab = savedTab;
    }
    this.loadData();
  }

  loadData(): void {
    this.loading = true;

    // Load all data
    this.adminService.getEmployees().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => (this.employees = data),
      error: (err) => console.error('Error loading employees', err),
    });

    this.adminService.getCustomers().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => (this.customers = data),
      error: (err) => console.error('Error loading customers', err),
    });

    this.adminService.getProjects().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => (this.projects = data),
      error: (err) => console.error('Error loading projects', err),
    });

    this.adminService.getTeams().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => (this.teams = data),
      error: (err) => console.error('Error loading teams', err),
    });

    this.adminService.getActivities().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.activities = data;
      },
      error: (err) => {
        console.error('Error loading activities', err);
      },
    });

    this.adminService.getUnits().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.units = data;
      },
      error: (err) => {
        console.error('Error loading units', err);
      },
    });

    this.adminService.getCostcenters().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.costcenters = data;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading costcenters', err);
        this.loading = false;
      },
    });
  }

  setActiveTab(tab: 'employee' | 'customer' | 'project' | 'activity' | 'activityPrice' | 'team' | 'salary' | 'bulkCreate' | 'audit'): void {
    this.activeTab = tab;
    this.viewState.setViewTab(this.viewKey, tab);
  }

  private isValidTab(tab: string | null): tab is 'employee' | 'customer' | 'project' | 'activity' | 'activityPrice' | 'team' | 'salary' | 'bulkCreate' | 'audit' {
    return tab === 'employee' || tab === 'customer' || tab === 'project' || tab === 'activity' || tab === 'activityPrice' || tab === 'team' || tab === 'salary' || tab === 'bulkCreate' || tab === 'audit';
  }

  // Called by child components when data changes
  onEmployeesUpdated(): void {
    this.adminService.getEmployees().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => (this.employees = data),
    });
  }

  onCustomersUpdated(): void {
    this.adminService.getCustomers().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => (this.customers = data),
    });
  }

  onProjectsUpdated(): void {
    this.adminService.getProjects().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => (this.projects = data),
    });
  }

  onActivitiesUpdated(): void {
    this.adminService.getActivities().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => (this.activities = data),
    });
  }

  onTeamsUpdated(): void {
    this.adminService.getTeams().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => (this.teams = data),
    });
  }
}
